import React from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import ColorPicker from "./ColorPicker";
import ImageUploader from "./ImageUploader";
import TextPropertyField from "./property-fields/TextPropertyField";
import ClassPropertyField from "./property-fields/ClassPropertyField";
import ObjectPropertyField from "./property-fields/ObjectPropertyField";
import ArrayPropertyField from "./property-fields/ArrayPropertyField";
import ImagePropertyField from "./property-fields/ImagePropertyField";
import LinkPropertyField from "./property-fields/LinkPropertyField";
import ButtonPropertyField from "./property-fields/ButtonPropertyField";
import IdPropertyField from "./property-fields/IdPropertyField";
import SelectPropertyField from "./property-fields/SelectPropertyField";
import DatePropertyField from "./property-fields/DatePropertyField";
import { updatePropertyWithClass, needsCssClass } from "../../utils/cssClassGenerator";
import { useEditorStore } from "../../stores/editorStore";

interface PropertyFieldProps {
  propName: string;
  prop: any;
  onChange: (value: any) => void;
  sectionName?: string;
  arrayIndex?: number;
}

// Mapeamento de tipos para componentes de edição
const TYPE_COMPONENTS = {
  // Campo de texto com html
  text: ({ value, onChange, placeholder, style, sectionName, propName, arrayIndex }: any) => {
    const { previewMode } = useEditorStore();
    // Normalizar o valor - pode vir como string direta ou objeto com value/style/link
    const isComposite = value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')
    const textValue = isComposite ? value.value : (value || '')
    const linkValue = isComposite && value.link ? value.link.value || value.link : ''

    // Resolver estilo considerando objeto responsivo
    let effectiveStyle: string = ''
    const rawStyle = isComposite ? value.style : style
    if (typeof rawStyle === 'string') {
      effectiveStyle = rawStyle
    } else if (rawStyle && typeof rawStyle === 'object') {
      effectiveStyle = rawStyle[previewMode] || ''
    }
    
    const handleChange = (newValue: any) => {
      if (sectionName && needsCssClass('text')) {
        const updatedValue = updatePropertyWithClass(sectionName, propName, value, newValue, arrayIndex);
        onChange(updatedValue);
      } else {
        // Preservar classe existente mesmo para tipos que não precisam de CSS
        if (value && typeof value === 'object' && value.class) {
          onChange({ ...newValue, class: value.class });
        } else {
          onChange(newValue);
        }
      }
    };
    
    return (
      <TextPropertyField
        value={textValue}
        onChange={handleChange}
        placeholder={placeholder}
        style={effectiveStyle}
        link={linkValue}
        onStyleChange={() => {}}
      />
    )
  },
  // Objeto genérico de props aninhadas
  object: ({ props: childProps, value, onChange, sectionName, propName }: any) => (
    <ObjectPropertyField 
      schema={childProps} 
      values={value || {}} 
      onChange={onChange}
      sectionName={sectionName}
      parentPropName={propName}
    />
  ),
  // Array genérico de objetos
  array: ({ props, value, onChange, sectionName, propName }: any) => (
    <ArrayPropertyField
      items={value || []}
      itemSchema={props}
      sectionName={sectionName}
      parentPropName={propName}
      onChange={(updated) => onChange(updated)}
    />
  ),
  // Campo de classe CSS
  class: ({ value, onChange, placeholder }: any) => (
    <ClassPropertyField value={value} onChange={onChange} placeholder={placeholder} />
  ),
  // Campo de imagem
  image: ({ value, onChange, placeholder }: any) => (
    <ImagePropertyField value={value} onChange={onChange} placeholder={placeholder} />
  ),
  // Campo de link
  link: ({ value, onChange, placeholder }: any) => (
    <LinkPropertyField value={value} onChange={onChange} placeholder={placeholder} />
  ),
  // Campo de ID simples
  id: ({ value, onChange, placeholder }: any) => (
    <IdPropertyField value={value} onChange={onChange} placeholder={placeholder} />
  ),
  // Campo de cor
  color: ({ value, onChange, placeholder, sectionName, propName, arrayIndex }: any) => {
    // Extrair a cor do CSS ou usar o valor direto
    let colorValue = ''
    if (typeof value === 'string') {
      colorValue = value
    } else if (value?.style) {
      // Extrair cor do CSS: "background-color: #c51a1a" -> "#c51a1a"
      const styleStr = typeof value.style === 'string' ? value.style : (value.style.desktop || '')
      const match = styleStr.match(/background-color:\s*(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3})/)
      colorValue = match ? match[1] : ''
    } else if (value?.value) {
      colorValue = value.value
    }
    
    const handleChange = (color: string) => {
      const newValue = { style: `background-color: ${color}` };
      if (sectionName && needsCssClass('color')) {
        const updatedValue = updatePropertyWithClass(sectionName, propName, value, newValue, arrayIndex);
        onChange(updatedValue);
      } else {
        // Preservar classe existente
        if (value && typeof value === 'object' && value.class) {
          onChange({ ...newValue, class: value.class });
        } else {
          onChange(newValue);
        }
      }
    };
    
    return (
      <ColorPicker 
        color={colorValue} 
        onChange={handleChange} 
      />
    )
  },
   // Campo de botão
   button: ({ value, onChange, placeholder, sectionName, propName, arrayIndex }: any) => (
     <ButtonPropertyField 
       value={value} 
       onChange={onChange} 
       placeholder={placeholder} 
       sectionName={sectionName}
       propName={propName}
       arrayIndex={arrayIndex}
     />
   ),
   select: ({ value, onChange, placeholder, options }: any) => {
     // Extrair o valor - pode vir como string direta ou objeto com value
     const selectValue = value && typeof value === 'object' && value.value ? value.value : (value || '')
     
     return (
       <SelectPropertyField
         value={selectValue}
         onChange={onChange}
         options={options || []}
         placeholder={placeholder}
       />
     )
   },
   // Campo de data
   date: ({ value, onChange, placeholder }: any) => (
     <DatePropertyField value={value} onChange={onChange} placeholder={placeholder} />
   ),
};

export default function PropertyField({ 
  propName, 
  prop, 
  onChange,
  sectionName,
  arrayIndex,
}: PropertyFieldProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { previewMode } = useEditorStore();

  // Resolver style para string baseada no modo de preview
  let resolvedStyle: string | undefined = undefined
  if (prop?.style && typeof prop.style === 'string') {
    resolvedStyle = prop.style
  } else if (prop?.value && typeof prop.value === 'object' && prop.value.style) {
    const st = prop.value.style
    if (typeof st === 'string') {
      resolvedStyle = st
    } else if (typeof st === 'object') {
      resolvedStyle = st[previewMode] || ''
    }
  }

  // Obter o componente baseado no tipo
  const FieldComponent =
    TYPE_COMPONENTS[prop.type as keyof typeof TYPE_COMPONENTS] ||
    TYPE_COMPONENTS.text;

  // Obter label e description do JSON ou usar fallbacks
  const label = prop.label || propName;
  const description = prop.description || "";

  return (
    <div className="border border-gray-200 rounded-md">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="property-button"
      >
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-2 bg-white rounded-b-lg space-y-2 property-expand">
          <p className="text-xs text-gray-500">{description}</p>
          <FieldComponent
            value={prop.value}
            props={prop.props}
            onChange={onChange}
            placeholder={description}
            options={prop.options}
            label={label}
            min={prop.min}
            max={prop.max}
            rows={prop.rows}
            style={resolvedStyle}
            sectionName={sectionName}
            propName={propName}
            arrayIndex={arrayIndex}
          />
        </div>
      )}
    </div>
  );
}
